import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { FileText, Image as ImageIcon, Download, Loader2, X, Paperclip } from "lucide-react";

export function AttachmentViewer({ ids = [], open, onClose }) {
  const [metas, setMetas] = useState([]);
  const [active, setActive] = useState(null);
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && ids.length) {
      api.post("/files/meta", { ids }).then((r) => {
        setMetas(r.data);
        if (r.data.length) openFile(r.data[0]);
      }).catch(() => {});
    }
    return () => { if (url) URL.revokeObjectURL(url); };
    // eslint-disable-next-line
  }, [open, JSON.stringify(ids)]);

  const openFile = async (m) => {
    setActive(m); setLoading(true);
    if (url) URL.revokeObjectURL(url);
    try {
      const res = await api.get(`/files/${m.id}/download`, { responseType: "blob" });
      setUrl(URL.createObjectURL(res.data));
    } finally { setLoading(false); }
  };

  if (!open) return null;
  const isImg = active?.content_type?.startsWith("image/");
  const isPdf = active?.content_type === "application/pdf";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl h-[90vh] p-0 flex flex-col" data-testid="attachment-viewer">
        <DialogHeader className="px-4 py-3 border-b"><DialogTitle className="text-sm">Attachments ({metas.length})</DialogTitle></DialogHeader>
        <div className="flex-1 flex min-h-0">
          <div className="w-56 border-r overflow-y-auto p-2 shrink-0">
            {metas.map((m) => (
              <button key={m.id} data-testid={`att-file-${m.id}`} onClick={() => openFile(m)}
                className={`w-full text-left px-3 py-2 rounded-lg text-xs flex items-center gap-2 mb-1 ${active?.id === m.id ? "bg-teal-50 text-teal-700 border border-teal-200" : "hover:bg-slate-100 dark:hover:bg-slate-800"}`}>
                {m.content_type?.startsWith("image/") ? <ImageIcon className="h-4 w-4 shrink-0" /> : <FileText className="h-4 w-4 shrink-0" />}
                <span className="truncate">{m.original_filename}</span>
              </button>
            ))}
          </div>
          <div className="flex-1 bg-slate-100 dark:bg-slate-950 flex items-center justify-center overflow-auto p-3">
            {loading ? <Loader2 className="h-6 w-6 animate-spin text-slate-400" /> :
              !url ? <span className="text-slate-400 text-sm">Select a file</span> :
              isImg ? <img src={url} alt={active.original_filename} className="max-h-full max-w-full object-contain rounded shadow-lg" data-testid="att-image" /> :
              isPdf ? <iframe src={url} title="pdf" className="w-full h-full rounded bg-white" data-testid="att-pdf" /> :
              <a href={url} download={active.original_filename} className="flex items-center gap-2 text-teal-600 bg-white px-4 py-2 rounded-lg shadow"><Download className="h-4 w-4" /> Download {active.original_filename}</a>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


export function AttachmentThumb({ ids = [], onClick, testid }) {
  const [thumb, setThumb] = useState(null);
  useEffect(() => {
    let alive = true; let obj;
    if (ids.length) {
      api.post("/files/meta", { ids: [ids[0]] }).then(async (r) => {
        if (!alive || !r.data.length) return;
        const m = r.data[0];
        if (m.content_type?.startsWith("image/")) {
          const res = await api.get(`/files/${m.id}/download`, { responseType: "blob" });
          obj = URL.createObjectURL(res.data); if (alive) setThumb(obj);
        }
      }).catch(() => {});
    }
    return () => { alive = false; if (obj) URL.revokeObjectURL(obj); };
    // eslint-disable-next-line
  }, [JSON.stringify(ids)]);
  if (!ids.length) return null;
  return (
    <button onClick={onClick} data-testid={testid} title="View attachments" className="relative inline-flex items-center">
      {thumb ? <img src={thumb} alt="proof" className="h-7 w-7 rounded object-cover border border-slate-200" />
        : <span className="h-7 w-7 rounded border border-slate-200 bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500"><Paperclip className="h-3.5 w-3.5" /></span>}
      {ids.length > 1 && <span className="absolute -top-1.5 -right-1.5 bg-teal-600 text-white text-[9px] rounded-full h-4 w-4 flex items-center justify-center">+{ids.length - 1}</span>}
    </button>
  );
}
